/**
 * Where the camera actually is, captured at the moment it goes on air.
 *
 * ## Why this is on the FEED and not the event
 *
 * An event's `latitude`/`longitude` is where somebody *planned* it. A heat map, a
 * coverage audit and "was the camera at the rally it claims to have filmed" all
 * want where the **device** was when it started publishing. Those are different
 * numbers and the second one is the evidence.
 *
 * ## Why "required" cannot mean "blocked"
 *
 * A browser can refuse geolocation outright, a desk laptop derives it from wifi
 * and can be tens of kilometres wrong, and a phone in a stadium can take twenty
 * seconds to get a fix. Making a broadcast impossible without one would mean the
 * rally goes unfilmed because a permission prompt was dismissed — and the
 * pressure that creates is somebody typing a plausible coordinate in by hand,
 * which is worse than no coordinate at all.
 *
 * So it is required in the sense that **it cannot be silently skipped**: either
 * a real fix with its accuracy, or an explicit recorded reason there is none.
 * An audit asking "where was this camera" gets an answer either way, and the
 * second answer is honest rather than absent.
 */

export interface CapturedLocation {
  latitude: number
  longitude: number
  /** Metres. A 2 km wifi fix and a 5 m GPS fix are not the same evidence. */
  accuracyM: number | null
  capturedAt: string
}

export type LocationOutcome =
  | { ok: true; location: CapturedLocation }
  | { ok: false; reason: string }

/** Long enough for a cold GPS fix in a crowd; short enough that nobody gives up. */
const TIMEOUT_MS = 20_000

export function captureLocation(): Promise<LocationOutcome> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return Promise.resolve({
      ok: false,
      reason: 'This browser does not provide location.',
    })
  }
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        ok: true,
        location: {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracyM: Number.isFinite(pos.coords.accuracy) ? Math.round(pos.coords.accuracy) : null,
          capturedAt: new Date(pos.timestamp).toISOString(),
        },
      }),
      (err) => resolve({ ok: false, reason: describe(err) }),
      // `enableHighAccuracy` asks for GPS rather than the wifi estimate. It costs
      // battery and time, and it is the difference between "this ward" and "this
      // venue" — which is the whole point of recording it.
      { enableHighAccuracy: true, timeout: TIMEOUT_MS, maximumAge: 60_000 },
    )
  })
}

function describe(err: GeolocationPositionError): string {
  switch (err.code) {
    case err.PERMISSION_DENIED:
      return 'Location permission was refused on this device.'
    case err.POSITION_UNAVAILABLE:
      return 'This device could not get a location fix.'
    case err.TIMEOUT:
      return 'Getting a location fix timed out.'
    default:
      return err.message || 'Location could not be read.'
  }
}

/**
 * How much to trust a fix, in words.
 *
 * A number alone invites the reader to treat 2,000 m and 8 m as the same fact.
 */
export function accuracyNote(accuracyM: number | null): string | null {
  if (accuracyM == null) return null
  if (accuracyM <= 30) return `±${accuracyM} m — a device fix, good enough to place a venue.`
  if (accuracyM <= 300) return `±${accuracyM} m — good enough for a ward, not for a venue.`
  return `±${(accuracyM / 1000).toFixed(1)} km — a network estimate. It places a town, not a place.`
}
